import { sql } from "drizzle-orm";
import {
  boolean,
  index,
  integer,
  jsonb,
  pgEnum,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
} from "drizzle-orm/pg-core";
import { tenant } from "../auth";

export const vendorListingVendorEnum = pgEnum("vendor_listing_vendor", [
  "STOCKX",
  "FANATICS",
  "MANUAL",
  "IN_HOUSE",
  "OTHER",
]);

/**
 * Whether a listing can be shown and browsed; only ACTIVE can. IMAGE_PROCESSING is a listing kept
 * hidden until its processed image is stored, and tenants see it as INACTIVE.
 */
export const vendorListingStatusEnum = pgEnum("vendor_listing_status", [
  "ACTIVE",
  "INACTIVE",
  "IMAGE_PROCESSING",
]);

export const vendorListings = pgTable(
  "vendor_listings",
  {
    id: text("id").primaryKey(),
    vendor: vendorListingVendorEnum("vendor").notNull(),
    title: text("title").notNull(),
    image: text("image"),
    attributes: jsonb("attributes"),
    /**
     * One typed entry per vendor variant (`ListingVariant` in fulfillment-core); null for
     * vendors that sell a single SKU.
     *
     * Writer: the StockX product-fetch mapper (`toListingVariantsAndPrice`), through promote,
     * auto-promote, the sourcing cron and the refresh worker; the sourcing step also copies a
     * sibling row's value. Each entry's `price` (acquisition cost) and `details.fee` (the
     * StockX fee) are tenant-private: `toListingResponse` removes them, so no tenant response
     * carries them. Tenants get `variantId`, `variantValue` and `details.sizeChart` only. The
     * staff dashboard reads the whole entry.
     */
    variants: jsonb("variants"),
    price: integer("price").notNull(),
    availableQty: integer("available_qty").notNull().default(0),
    /**
     * Units of this listing our own marketplace orders hold: raised by a sale, lowered by its
     * cancel (`order/apply-deltas.ts`), as Boxed's `salesCount`. A listing with any is sold and
     * never comes back; one Fanatics took down with none can (`fanatics-scrape/select-writes.ts`).
     *
     * Writer: the order stock path and the Boxed listing backfill only. Every other listing write
     * leaves it as stored (`EXCLUDED_SET` in `listing/batch-upsert.ts`).
     */
    salesCount: integer("sales_count").notNull().default(0),
    status: vendorListingStatusEnum("status").notNull().default("ACTIVE"),
    tags: text("tags").array().notNull().default([]),
    category: text("category"),
    scrapedAt: timestamp("scraped_at", { withTimezone: true }),
    /** Written on every refresh attempt, successful or not, so a failing row cannot hold the head of the queue. */
    refreshAttemptedAt: timestamp("refresh_attempted_at", { withTimezone: true }),
    /**
     * When a StockX refresh worker last found this row's product page gone (404 or 410) with
     * nothing to contradict it. The worker does not deactivate: the next refresh cron run
     * deactivates the reported products together, within its per-run cap. A report older than
     * `refresh_attempted_at` is out of date, because a later attempt has read the product since.
     * Every `upsertListing` write clears it, since the listing writers never set it.
     */
    goneReportedAt: timestamp("gone_reported_at", { withTimezone: true }),
    /** No longer written or read: the refresh worker stopped rendering. Dropped by the contract migration. */
    renderFailedAt: timestamp("render_failed_at", { withTimezone: true }),
    /**
     * When a StockX refresh worker last found this row's product missing from the Public API:
     * the market data and the product read both answered 404. Written on every row of the
     * product. The next refresh that finds it missing again reports the product gone; a refresh
     * that finds it clears the column on every row, and so does every `upsertListing` write.
     */
    notFoundAt: timestamp("not_found_at", { withTimezone: true }),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
    expiresAt: timestamp("expires_at", { withTimezone: true }),
    /** Owning tenant. NULL means the listing is shared across every tenant. */
    tenantId: text("tenant_id").references(() => tenant.id, { onDelete: "cascade" }),
    /** No longer written or read; `GET /catalog/in-box` answers from `drop_table_items`. Dropped by the contract migration. */
    isInBox: boolean("is_in_box"),
    onMarketplace: boolean("on_marketplace").notNull().default(true),
  },
  (t) => [
    // Boxed's `E1#<attributes.listingId>` pk (hydrate, order stock effects) must name one row.
    uniqueIndex("vendor_listings_fanatics_algolia_listing_id_idx")
      .on(sql`(${t.attributes}->>'listingId')`)
      .where(sql`${t.vendor} = 'FANATICS'`),
    // A StockX row's product is its id's second `#` segment (`STOCKX#<urlKey>[#v:…][#t:…]`); the
    // refresh worker and the sourcing cron read every row of a product by it. No partial WHERE:
    // the queries bind the vendor as a parameter, which a generic plan cannot match to one.
    index("vendor_listings_id_second_segment_idx").on(sql`split_part(${t.id}, '#', 2)`),
    // The console's list of rows waiting for a processed image reads only these rows, oldest
    // `updated_at` first. The predicate names IMAGE_PROCESSING as "neither ACTIVE nor INACTIVE":
    // migration 0123 adds that value, the Migrator applies every pending migration in one
    // transaction, and Postgres refuses a new enum value in the transaction that adds it.
    index("vendor_listings_awaiting_processed_image_idx")
      .on(t.vendor, t.updatedAt)
      .where(
        sql`(${t.status} <> 'ACTIVE' and ${t.status} <> 'INACTIVE') or (${t.status} = 'ACTIVE' and ${t.attributes} ? 'copiedFrom')`,
      ),
  ],
);
